import { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useMuiModal } from '../../../_core/hooks';
import { CreateUserDto, UpdateUserDto, UserDto } from '@families-link/shared';
import { ConfirmationModal, ConfirmationModalProps } from '../../../_core/components/confirmation-modal';
import { buttonModalWindowStyles } from '../../../_core/styles';
import { Theme } from '@mui/material';

export type useOpenConfirmModalFormHookProps = {
	featchAction(values: CreateUserDto | UpdateUserDto): Promise<void>;
	handlePagination(): void;
	title: string;
	description?: string;
	icon?: ReactNode;
	danger?: boolean;
	info?: boolean;
	submitButtonName?: string;
	toastText?: string;
	customBackgrondColorKey?: keyof Theme['colors'];
	customColorKey?: keyof Theme['colors'];
	customBorderColorKey?: keyof Theme['colors'];
};

export function useOpenConfirmModal(props: useOpenConfirmModalFormHookProps) {
	const {
		featchAction,
		handlePagination,
		title,
		description,
		icon,
		danger = false,
		info=false,
		submitButtonName,
		toastText,
		customBackgrondColorKey,
		customBorderColorKey,
		customColorKey,
	} = props;
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();

	const showToast = (message: string, variant: 'error' | 'default' | 'success' | 'warning' | 'info' | undefined) => {
		enqueueSnackbar(message, {
			variant,
		});
	};

	const { openModal, closeModal } = useMuiModal<ConfirmationModalProps>(ConfirmationModal);
	const handleConfirm = async (user: UserDto): Promise<void> => {
		try {
			await featchAction(user);
			showToast(toastText || 'Successfully performed', 'success');
			handlePagination();
			closeModal();
		} catch (error) {
			console.error(error);
		}
	};

	const handleOpenConfirmModal = useCallback(
		(user: UserDto) => {
			const handleSubmit = async () => {
				await handleConfirm(user);
			};
			openModal({
				title,
				description: description?.replace(':UserName', user.name),
				onSubmit: handleSubmit,
				onClose: closeModal,
				icon,
				danger,
				customBackgrondColorKey: customBackgrondColorKey,
				customBorderColorKey: customBorderColorKey,
				customColorKey: customColorKey,
				leftButtonProps: {
					children: t('users.forms.buttons.cancel'),
					variant: 'secondary',
					sx: buttonModalWindowStyles,
				},
				rightButtonProps: {
					children: submitButtonName,
					variant: danger? 'danger': info? 'info' : 'primary',
					contraModeStyleButton: true,
					sx: buttonModalWindowStyles,
				},
			});
		},
		[handleConfirm, openModal]
	);

	return handleOpenConfirmModal;
}
